import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/core/services/language.service';
import { UserAgentService } from './user-agent.service';
import { Howl, Howler } from 'howler';
import { AudioLog, IOperation, OPERATION_TYPE } from 'src/app/user-identification/models/user-identification';
import { OperationService } from './step.service';
import { ConfigService } from './config/config.service';
import { CloudLogsService, LogType } from './cloud-logs.service';

/** Coincide con rutas tipo `./assets/audios/es/...` */
const ASSETS_AUDIOS_SEGMENT = 'assets/audios/';

/** Locales con bundle propio en `src/assets/audios/<locale>/` (debe alinearse con carpetas reales). */
const AUDIO_BUNDLE_LOCALES: readonly string[] = ['es', 'es-CO', 'en', 'fr', 'pt', 'it'];

/** Qué bundle de guía recargar tras `onLangChange` (según operación actual en user-identification). */
export type GuideAudioReloadScope = 'ocr' | 'life' | 'none';

export enum Audios {
	Success = 'success',
}

/** Evita parchear dos veces el mismo AudioContext (Howler puede recrear el servicio en tests). */
const guardedAudioContexts = new WeakSet<object>();

type ResumeGuardedAudioContext = AudioContext & {
	resume: () => Promise<void>;
};

@Injectable({
	providedIn: 'root',
})
export class AudioService {
	private audioCache: Map<string, Howl> = new Map();
	private currentSound: Howl | null = null;

	public audiolog: string[] = [];
	public audioGuideIsEnabled: boolean = true;
	private audioQueue: string[] = [];
	public isPlaying: boolean = false;
	private operations: IOperation[] = [];
	private lang = inject(LanguageService);
	private userAgent = inject(UserAgentService);
	private trans = inject(TranslateService);
	private operationService = inject(OperationService);
	private configService = inject(ConfigService);
	private destroyRef = inject(DestroyRef);
	private cloudLogsService = inject(CloudLogsService);

	/** Operación activa: define si tras cambiar idioma se precarga OCR, life-check o solo finish. */
	private guideReloadScope: GuideAudioReloadScope = 'none';

	/** Anti-spam Cloud: mismo fallo de resume en ventana corta (iOS interrupted). */
	private lastResumeFailKey = '';
	private lastResumeFailAt = 0;
	private static readonly RESUME_FAIL_DEDUP_MS = 10_000;

	constructor() {
		// Configurar Howler para usar Web Audio API preferentemente
		Howler.usingWebAudio = true;
		// NIF-1034: Howler.ctx puede ser null hasta el primer Howl; se reintenta en play/preload.
		this.guardHowlerAudioContextResume();

		this.operationService
			.getOperations()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((operations) => {
				this.operations = operations || [];
			});

		this.trans.onLangChange.subscribe(() => {
			this.onLanguageChangedReloadAudios();
		});
	}

	/**
	 * NIF-1034: atrapa el reject de `AudioContext.resume()` (WebKit iOS:
	 * "Failed to start the audio device") para que no quede unhandled → Zone → Cloud ERROR.
	 * Howler 2.2.4 llama resume sin .catch en _autoResume.
	 */
	private guardHowlerAudioContextResume(): void {
		try {
			const ctx = Howler.ctx as ResumeGuardedAudioContext | null | undefined;
			if (!ctx || typeof ctx.resume !== 'function' || guardedAudioContexts.has(ctx)) {
				return;
			}
			guardedAudioContexts.add(ctx);
			const originalResume = ctx.resume.bind(ctx);
			ctx.resume = (): Promise<void> => {
				try {
					const result = originalResume();
					if (result != null && typeof (result as Promise<void>).then === 'function') {
						return (result as Promise<void>).catch((err: unknown) => {
							const msg = err instanceof Error ? err.message : String(err);
							this.addLog(`AudioContext.resume failed: ${msg}`);
							this.logAudioCloud(AudioLog.RESUME_FAIL, { reason: 'resume_reject', message: msg }, LogType.Warning);
						});
					}
					return Promise.resolve();
				} catch (err: unknown) {
					const msg = err instanceof Error ? err.message : String(err);
					this.addLog(`AudioContext.resume threw: ${msg}`);
					this.logAudioCloud(AudioLog.RESUME_FAIL, { reason: 'resume_throw', message: msg }, LogType.Warning);
					return Promise.resolve();
				}
			};
		} catch {
			// Sin AudioContext (SSR / tests sin Web Audio): no-op.
		}
	}

	/**
	 * Debe llamarse cuando cambie la operación actual (p. ej. desde user-identification)
	 * para que al cambiar idioma solo se precarguen los audios del flujo en curso.
	 */
	public setGuideAudioReloadScope(scope: GuideAudioReloadScope): void {
		this.guideReloadScope = scope;
	}

	/**
	 * Al cambiar el idioma: detiene reproducción, libera Howls bajo assets/audios y
	 * vuelve a precargar FINISH siempre y el bundle de guía según el scope actual.
	 */
	private onLanguageChangedReloadAudios(): void {
		this.stop();
		this.clearCachedAudiosUnderAssetsPath();
		this.syncGuideAudioReloadScope();
		void this.reloadGuideAudiosForCurrentLanguage();
	}

	private clearCachedAudiosUnderAssetsPath(): void {
		for (const [url, howl] of [...this.audioCache.entries()]) {
			if (url.includes(ASSETS_AUDIOS_SEGMENT)) {
				howl.unload();
				this.audioCache.delete(url);
			}
		}
	}

	private async reloadGuideAudiosForCurrentLanguage(): Promise<void> {
		try {
			await this.preloadFinishAudios();
			if (this.guideReloadScope === 'ocr') {
				await this.preloadOcrMrzAudios();
			} else if (this.guideReloadScope === 'life') {
				await this.preloadLifeCheckAudios();
			}
		} catch (e) {
			console.error('Error reloading audios after language change:', e);
		}
	}

	/**
	 * Indica al AudioService qué bundle precargar al cambiar idioma según la operación activa.
	 */
	private syncGuideAudioReloadScope(): void {
		const current = this.operations?.find((op: IOperation) => !op.completed);
		let scope: GuideAudioReloadScope = 'none';
		if (current) {
			switch (current.type) {
				case OPERATION_TYPE.OCR:
				case OPERATION_TYPE.OCR_CANNY:
					scope = 'ocr';
					break;
				case OPERATION_TYPE.POL:
					scope = 'life';
					break;
				default:
					scope = 'none';
			}
		}
		this.setGuideAudioReloadScope(scope);
	}

	public playAudio(code: string): void {
		if (!code || this.audioQueue.includes(code)) return;

		const url = this.getUrl(code);
		this.addLog(`Se quiere escuchar el audio: ${code}`);
		if (!this.audioGuideIsEnabled) {
			this.addLog(`Audio no habilitado: ${code}`);
			return;
		}
		if (!this.audioCache.has(url)) {
			this.addLog(`Audio no encontrado en caché, intentando cargar al vuelo: ${code}`);
			this.preloadAudio(url);
		}

		this.audioQueue.push(url);
		this.processAudioQueue();
	}

	private async processAudioQueue(): Promise<void> {
		if (this.isPlaying || !this.audioQueue.length) {
			return;
		}

		const url = this.audioQueue[0];
		this.playHowl(url);
	}

	private playHowl(url: string): void {
		// Howler crea el ctx en el primer Howl; reaplicar guard por si aún no existía.
		this.guardHowlerAudioContextResume();

		let sound = this.audioCache.get(url);

		if (!sound) {
			// Si no está en caché (ej: URL externa de TTS), crearlo
			sound = new Howl({
				src: [url],
				html5: false, // Forzar Web Audio API para canal multimedia
				onloaderror: (_id, err) => {
					this.addLog(`Error cargando audio ${url}: ${err}`);
					this.logAudioCloud(
						AudioLog.LOAD_ERROR,
						{ url, err: String(err), phase: 'play_create' },
						LogType.Warning,
					);
					this.terminateAudioPlayback();
				},
			});
			// No lo guardamos en caché si es dinámico (TTS) para no llenar memoria
			if (!url.includes('google')) {
				this.audioCache.set(url, sound);
			}
			this.guardHowlerAudioContextResume();
		}

		this.isPlaying = true;
		this.currentSound = sound;

		sound.once('end', () => {
			this.addLog(`Audio terminado: ${url}`);
			this.terminateAudioPlayback();
		});

		// NIF-1034: no esperar unlock indefinido (dejaba isPlaying=true y bloqueaba la cola).
		sound.once('playerror', (_id, err) => {
			this.addLog(`Error reproduciendo audio ${url}: ${err}`);
			this.logAudioCloud(
				AudioLog.PLAY_ERROR,
				{
					url,
					err: String(err),
					ctxState: Howler.ctx?.state ?? null,
					queueLen: this.audioQueue.length,
				},
				LogType.Warning,
			);
			this.terminateAudioPlayback();
		});

		sound.play();
		this.addLog(`Reproduciendo: ${url}`);
	}

	/**
	 * NIF-1034: Cloud Logs quirúrgicos (addLog/audiolog no llega a Google).
	 * Resume se deduplica 10s para no spamear en iOS interrupted.
	 */
	private logAudioCloud(
		info: AudioLog,
		data: Record<string, unknown>,
		type: LogType.Warning | LogType.Error,
	): void {
		if (info === AudioLog.RESUME_FAIL) {
			const key = String(data['message'] ?? data['reason'] ?? '');
			const now = Date.now();
			if (key && key === this.lastResumeFailKey && now - this.lastResumeFailAt < AudioService.RESUME_FAIL_DEDUP_MS) {
				return;
			}
			this.lastResumeFailKey = key;
			this.lastResumeFailAt = now;
		}
		this.cloudLogsService.sendCloudLogs(
			{
				...data,
				guideEnabled: this.audioGuideIsEnabled,
				isPlaying: this.isPlaying,
			},
			info,
			type,
		);
	}

	/**
	 * Detiene la reproducción de todos los audios en cola.
	 */
	public stop(): void {
		this.audioQueue = [];
		if (this.currentSound) {
			this.currentSound.stop();
			this.currentSound = null;
		}
		this.isPlaying = false;
	}

	private terminateAudioPlayback(): void {
		this.isPlaying = false;
		this.currentSound = null;
		// Elimina el audio reproducido
		this.audioQueue.shift();
		// Procesa el siguiente audio en la cola
		this.processAudioQueue();
	}

	public async preloadLifeCheckAudios(): Promise<void> {
		const preloadAudios = [
			this.getUrl(Audios.Success),
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.MIDDLE_FACE.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.SMILE.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.SMILE.TEXT.ONWARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.SMILE.TEXT.ONWARNING_TRANSITION.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.SMILE.TEXT.ONCOMPLETE.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONWARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONCOMPLETE.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.TURN_LEFT_HEAD.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.LIFT_HEAD.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.ONWARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.ONFAIL.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.MISMATCH_WARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.MANUAL.TAKE_PHOTO.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.MANUAL.VALIDATION_PHOTO.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.IS_TOO_FAR_1.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.IS_TOO_CLOSE_1.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_1.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_2.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.TURN_CENTER_AND_TURN.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.HAND_GESTURE.THUMB_UP.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.HAND_GESTURE.VICTORY.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.HAND_GESTURE.POINTING_UP.TEXT.ONINIT.mp3`,
		];

		try {
			await Promise.all(preloadAudios.map((url) => this.preloadAudio(url)));
			this.addLog('Todos los audios de LIFE_CHECK cargados correctamente');
		} catch (error) {
			console.error('Error preloading LIFE_CHECK audios:', error);
		}
	}

	/**
	 * Precarga solo los audios de instrucción para el flujo "sacar foto" en documentos (genérico).
	 * No incluye el bundle completo OCR/MRZ para no cargar archivos innecesarios.
	 */
	public async preloadDocsTakePhotoAudios(): Promise<void> {
		const preloadAudios = [
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_DOC.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.CHECK_INFO.mp3`,
		];

		try {
			await Promise.all(preloadAudios.map((url) => this.preloadAudio(url)));
			this.addLog('Audios de docs take photo (genérico) cargados');
		} catch (error) {
			console.error('Error preloading docs take photo audios:', error);
		}
	}

	/**
	 * Precarga únicamente los audios del carnet de conducir (dos caras): delantera y trasera.
	 * Solo debe invocarse en ese flujo para no mezclar con otros tipos de documento.
	 */
	public async preloadDocsDriveLicenseTakePhotoAudios(): Promise<void> {
		const preloadAudios = [
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_FRONT_FIRST.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_BACK_SECOND.mp3`,
		];

		try {
			await Promise.all(preloadAudios.map((url) => this.preloadAudio(url)));
			this.addLog('Audios de docs take photo (carnet dos caras) cargados');
		} catch (error) {
			console.error('Error preloading docs drive license take photo audios:', error);
		}
	}

	/** Solo instrucciones EUDI (rápido; para locutar al abrir wallet sin esperar todo el bundle OCR). */
	public async preloadEudiInstructionAudios(): Promise<void> {
		try {
			await Promise.all(this.getEudiInstructionAudioUrls().map((url) => this.preloadAudio(url)));
		} catch (error) {
			console.error('Error preloading EUDI instruction audios:', error);
		}
	}

	private getEudiInstructionAudioUrls(): string[] {
		const lang = this.getLanguage();
		return [
			`./assets/audios/${lang}/OCR.EUDI.INSTRUCTION_DESKTOP.mp3`,
			`./assets/audios/${lang}/OCR.EUDI.INSTRUCTION_MOBILE.mp3`,
		];
	}

	public async preloadOcrMrzAudios(): Promise<void> {
		const preloadAudios = [
			this.getUrl(Audios.Success),
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.ONWARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/LIFE_CHECK.GENERAL.TEXT.ONFAIL.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.BACK.INSTRUCTIONS.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.BACK.INSTRUCTIONS.TEXT.ONWARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.INSTRUCTIONS.TEXT.ONINIT_ID.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.INSTRUCTIONS.TEXT.ONINIT_ID_PASSPORT.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.INSTRUCTIONS.TEXT.ONWARNING_FIRST_SCAN_ID.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.INSTRUCTIONS.TEXT.ONWARNING_FIRST_SCAN.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.SELECT_DOCUMENT_TYPE.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.CHECK_INFO.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_SINGLE.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_DOC.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_FRONT_FIRST.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_FRONT_SECOND.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_BACK_FIRST.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MANUAL.TAKE_PHOTO_BACK_SECOND.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.DONT_READ_DOCUMENT.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.ERROR.DOC_TYPE_NOT_ALLOWED.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.ERROR.COUNTRY_NOT_ALLOWED.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.MODE_SEMI_MANUAL.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.FLEXIBLE.UPLOAD_ATTACH_FRONT.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.FLEXIBLE.UPLOAD_ATTACH_BACK.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.GENERAL.CHANGE_SIDE.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.SCANNER_INSTRUCTION.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.SCANNER_INSTRUCTION_MOBILE.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.PROMPT_FULL_QR_REMINDER.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.PROMPT_1.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.PROMPT_2.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.PROMPT_3.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.PROMPT_4.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.MIDNI.PROMPT_5.mp3`,
			...this.getEudiInstructionAudioUrls(),
			`./assets/audios/${this.getLanguage()}/OCR.BARCODE.INSTRUCTION.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_BLURRY_1.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_BLURRY_3.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_BLURRY_WITH_MULTIPLE_CAMERAS.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_OVEREXPOSED_1.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_OVEREXPOSED_2.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_TOO_CLOSE_1.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_TOO_CLOSE_2.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_TOO_FAR_1.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.IS_TOO_FAR_2.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.KEEP_STEADY.mp3`,
			`./assets/audios/${this.getLanguage()}/OCR.AUDIO.NO_FACE_IN_DOCUMENT.mp3`,
		];

		try {
			await Promise.all(preloadAudios.map((url) => this.preloadAudio(url)));
			this.addLog('Todos los audios de OCR MRZ cargados correctamente');
		} catch (error) {
			console.error('Error preloading OCR MRZ audios:', error);
		}
	}

	public async preloadFinishAudios(): Promise<void> {
		const preloadAudios = [
			`./assets/audios/${this.getLanguage()}/FINISH.KO_TITLE.mp3`,
			`./assets/audios/${this.getLanguage()}/FINISH.OK_TITLE.mp3`,
			`./assets/audios/${this.getLanguage()}/FINISH.OK_PENDING_TITLE.mp3`,
		];

		try {
			await Promise.all(preloadAudios.map((url) => this.preloadAudio(url)));
			this.addLog('Todos los audios de FINISH cargados correctamente');
		} catch (error) {
			console.error('Error preloading FINISH audios:', error);
		}
	}

	private async preloadAudio(url: string): Promise<void> {
		if (this.audioCache.has(url)) return;

		return new Promise((resolve) => {
			const sound = new Howl({
				src: [url],
				preload: true,
				html5: false, // Usar Web Audio API
				onload: () => {
					this.addLog(`Audio cargado: ${url}`);
					this.guardHowlerAudioContextResume();
					resolve();
				},
				onloaderror: (_id, err) => {
					this.addLog(`Error cargando audio ${url}: ${err}`);
					// Sin Cloud en preload (NIF-1034): spamearía un log por cada MP3 ausente.
					// No rechazamos para que Promise.all no falle por un solo audio
					resolve();
				},
			});
			this.audioCache.set(url, sound);
			this.guardHowlerAudioContextResume();
		});
	}

	/**
	 * Precarga audio para MÓVILES usando Web Audio API y AudioBuffers.
	 * Garantiza que todos los audios usen el mismo canal de salida.
	 */
	private async preloadAudioMobile(url: string): Promise<void> {
		// Deprecated implementation replaced by Howler
		this.preloadAudio(url);
	}

	/**
	 * Precarga audio para DESKTOP usando HTMLAudioElement.
	 * Funciona perfectamente en escritorio.
	 * HTMLAudioElement carga automáticamente en background.
	 */
	private preloadAudioDesktop(url: string): void {
		// Deprecated implementation replaced by Howler
		this.preloadAudio(url);
	}

	public playSuccessAudio(): void {
		// Detener cualquier reproducción actual y limpiar la cola
		this.stop();
		// Añadir solo el audio de éxito a la cola
		this.audioQueue.push(this.getUrl(Audios.Success));
		// Procesar la cola para reproducir
		this.processAudioQueue();
	}

	public hasAudio(code: string): boolean {
		const url = this.getUrl(code);
		return this.audioCache.has(url);
	}

	public disabledAudioGuide(): void {
		this.audioGuideIsEnabled = false;
	}

	public enabledAudioGuide(): void {
		this.audioGuideIsEnabled = true;
	}

	private getUrl(code: string): string {
		if (code === Audios.Success) {
			return './assets/audios/success.mp3';
		}
		return `./assets/audios/${this.getLanguage()}/${code}.mp3`;
	}

	private getLanguage(): string {
		const code = this.lang.getLanguage();
		if (code === 'es') {
			const companyCountry = this.configService.getCurrentConfig()?.company_country;
			if ((companyCountry || '').toUpperCase() === 'COL') {
				return 'es-CO';
			}
		}
		return AUDIO_BUNDLE_LOCALES.includes(code) ? code : 'en';
	}

	private addLog(text): void {
		if (!Array.isArray(this.audiolog)) {
			this.audiolog = [];
		}
		this.audiolog.push(text);
	}
}
